import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, Grid, Card, CardContent,
  TextField, FormControl, InputLabel, Select, MenuItem,
  CircularProgress, Alert, Divider, Dialog, DialogTitle,
  DialogContent, DialogActions
} from '@mui/material';
import { ArrowBack, CurrencyExchange, SwapHoriz, CheckCircle } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getAccountBalance, exchangeCurrency } from 'api/wallet';
import { ClientGetLatestExchangeRates } from 'api/client';

const CURRENCIES = ['THB', 'LAK', 'USD'];
const SYMBOLS = { USD: '$', THB: '฿', LAK: '₭' };
const FLAGS = { USD: 'us', THB: 'th', LAK: 'la' };

const CurrencyFlag = ({ currency }) => (
  <img
    src={`https://flagcdn.com/16x12/${FLAGS[currency] || 'un'}.png`}
    alt={currency}
    style={{ width: 16, height: 12, verticalAlign: 'middle', borderRadius: 1, marginRight: 4 }}
  /> 
);

const WalletExchange = () => {
  const navigate = useNavigate();

  const [balance, setBalance] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successDialog, setSuccessDialog] = useState(false);
  const [successData, setSuccessData] = useState(null);

  const [fromCurrency, setFromCurrency] = useState('USD');
  const [toCurrency, setToCurrency] = useState('THB');
  const [amount, setAmount] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [balData, rateData] = await Promise.all([
          getAccountBalance(),
          ClientGetLatestExchangeRates(),
        ]);
        setBalance(balData.data);
        setExchangeRate(rateData?.data || rateData);
      } catch (err) {
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const toTHB = (val, cur) => {
    if (!exchangeRate) return 0;
    if (cur === 'THB') return val;
    if (cur === 'USD') return val * (exchangeRate.USD || 0);
    if (cur === 'LAK') return val / (exchangeRate.LAK || 1);
    return 0;
  };

  const fromTHB = (val, cur) => {
    if (!exchangeRate) return 0;
    if (cur === 'THB') return val;
    if (cur === 'USD') return val / (exchangeRate.USD || 1);
    if (cur === 'LAK') return val * (exchangeRate.LAK || 0);
    return 0;
  };

  const parsedAmount = parseFloat(amount) || 0;
  const thbAmount = toTHB(parsedAmount, fromCurrency);
  const receivedAmount = Math.floor(fromTHB(thbAmount, toCurrency) * 100) / 100;
  const rate = parsedAmount > 0 ? receivedAmount / parsedAmount : 0;

  const fromBalance = balance?.balances?.[fromCurrency] || 0;
  const hasEnough = parsedAmount > 0 && fromBalance >= parsedAmount;
  const canSubmit = parsedAmount > 0 && fromCurrency !== toCurrency && hasEnough && exchangeRate;

  const swapCurrencies = () => {
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
    setAmount('');
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      setSubmitting(true);
      setError('');
      const result = await exchangeCurrency(fromCurrency, toCurrency, parsedAmount);
      setSuccessData(result.data);
      setSuccessDialog(true);
      setAmount('');
      // Refresh balance
      const balData = await getAccountBalance();
      setBalance(balData.data);
    } catch (err) {
      setError(err.message || 'Exchange failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m={2}>
      <Box display="flex" alignItems="center" mb={3} sx={{ position: 'relative' }}>
        <Button color="secondary" startIcon={<ArrowBack />} onClick={() => navigate('/customer-portal/wallet')}>
          Back
        </Button>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
          <CurrencyExchange sx={{ mr: 1 }} />
          Currency Exchange
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Grid container spacing={3}>
        {/* Exchange Form */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Exchange</Typography>

            {/* From */}
            <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={5}>
                  <FormControl fullWidth>
                    <InputLabel>From</InputLabel>
                    <Select value={fromCurrency} onChange={(e) => setFromCurrency(e.target.value)} label="From">
                      {CURRENCIES.filter((c) => c !== toCurrency).map((c) => (
                        <MenuItem key={c} value={c}>
                          <CurrencyFlag currency={c} />{c}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={7}>
                  <TextField
                    fullWidth label="Amount" type="number"
                    inputProps={{ min: 0.01, step: 0.01 }}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    error={parsedAmount > 0 && !hasEnough}
                    helperText={parsedAmount > 0 && !hasEnough ? `Insufficient balance (${SYMBOLS[fromCurrency]}${fromBalance.toLocaleString()})` : `Balance: ${SYMBOLS[fromCurrency]}${fromBalance.toLocaleString()}`}
                  />
                </Grid>
              </Grid>
            </Card>

            {/* Swap Button */}
            <Box display="flex" justifyContent="center" my={1}>
              <Button variant="outlined" color="secondary" startIcon={<SwapHoriz />} onClick={swapCurrencies} size="small">
                Swap
              </Button>
            </Box>

            {/* To */}
            <Card variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={5}>
                  <FormControl fullWidth>
                    <InputLabel>To</InputLabel>
                    <Select value={toCurrency} onChange={(e) => setToCurrency(e.target.value)} label="To">
                      {CURRENCIES.filter((c) => c !== fromCurrency).map((c) => (
                        <MenuItem key={c} value={c}>
                          <CurrencyFlag currency={c} />{c}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={7}>
                  <TextField
                    fullWidth label="You receive (estimated)" value={parsedAmount > 0 ? receivedAmount.toLocaleString() : ''}
                    InputProps={{ readOnly: true }}
                    helperText={parsedAmount > 0 && rate > 0 ? `Rate: 1 ${fromCurrency} ≈ ${rate.toFixed(4)} ${toCurrency}` : ' '}
                    sx={{ '& .MuiInputBase-input': { color: 'success.main', fontWeight: 'bold' } }}
                  />
                </Grid>
              </Grid>
            </Card>

            <Button
              fullWidth variant="contained" color="secondary" size="large"
              startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <CurrencyExchange />}
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
            >
              {submitting ? 'Processing...' : `Exchange ${SYMBOLS[fromCurrency]}${parsedAmount > 0 ? parsedAmount.toLocaleString() : '—'} ${fromCurrency}`}
            </Button>

            {!exchangeRate && (
              <Alert severity="warning" sx={{ mt: 2 }}>Exchange rates not available. Please try again later.</Alert>
            )}
          </Paper>
        </Grid>

        {/* Balance Overview */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Current Balance</Typography>
            {CURRENCIES.map((c) => (
              <Card key={c} variant="outlined" sx={{ mb: 1.5 }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center">
                      <CurrencyFlag currency={c} />
                      <Typography variant="body1">{c}</Typography>
                    </Box>
                    <Typography variant="h6" fontWeight="bold">
                      {SYMBOLS[c]}{(balance?.balances?.[c] || 0).toLocaleString()}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}

            {exchangeRate && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Exchange Rates (base: THB)
                </Typography>
                <Typography variant="body2">1 USD = ฿{exchangeRate.USD}</Typography>
                <Typography variant="body2">฿1 THB = ₭{exchangeRate.LAK}</Typography>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Success Dialog */}
      <Dialog open={successDialog} onClose={() => setSuccessDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircle color="success" />
          Exchange Successful
        </DialogTitle>
        <DialogContent>
          {successData && (
            <Box sx={{ textAlign: 'center', py: 1 }}>
              <Typography variant="h5" gutterBottom>
                {SYMBOLS[successData.fromCurrency]}{successData.givenAmount?.toLocaleString()} {successData.fromCurrency}
              </Typography>
              <SwapHoriz sx={{ fontSize: 36, color: 'text.secondary', my: 1 }} />
              <Typography variant="h4" color="success.main">
                {SYMBOLS[successData.toCurrency]}{successData.receivedAmount?.toLocaleString()} {successData.toCurrency}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="secondary" onClick={() => setSuccessDialog(false)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WalletExchange;
